import React from 'react';
import { useSelector } from 'react-redux';
import { hexToRgb } from '@nykaa/ui-components/styles/utils';
import styled from 'App/styled';
import {
  selectProductMedia,
  selectProductRelatedVideo,
} from 'shared/store/product/selector';
import { State } from 'shared/store/types';
import PlayButtonFill from 'components/Icons/PlayButtonFill';
import { convertSecondsToDisplayTimer, DisplayUpTo } from 'helpers/utils/time';

interface MediaSectionProps {
  selectedImage: any;
  selectedVideo: any;
  setSelectedImage: (args: any) => void;
  setSelectedVideo: (args: any) => void;
  isVideoTab: boolean;
}

const ItemsWrapper = styled.div<{ isVideo: boolean }>`
  padding: ${({ isVideo, theme }) => {
    const { spacing120 } = theme.spacing;
    return isVideo
      ? `${spacing120} ${spacing120} 10px ${spacing120}`
      : `${spacing120} 0 10px ${spacing120}`;
  }};
  display: flex;
  flex-wrap: wrap;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  align-content: flex-start;
  justify-content: ${({ isVideo }) => (isVideo ? 'center' : 'flex-start')};
  ::-webkit-scrollbar {
    width: 5px;
  }
  ::-webkit-scrollbar-track {
    background-color: ${({ theme, isVideo }) =>
      !isVideo
        ? hexToRgb(theme.colors.textPrimary, 0.16)
        : hexToRgb(theme.colors.surface, 0.16)};
    -webkit-border-radius: 10px;
    border-radius: 10px;
  }
  ::-webkit-scrollbar-thumb {
    -webkit-border-radius: 10px;
    border-radius: 10px;
    background: ${({ theme, isVideo }) =>
      !isVideo
        ? hexToRgb(theme.colors.textPrimary, 0.36)
        : hexToRgb(theme.colors.surface, 0.44)};
  }
  div:nth-of-type(3n) {
    margin: ${({ isVideo, theme }) =>
      !isVideo ? `0 0 ${theme.spacing.spacing120} 0` : 0};
  }
`;

const ImageWrapper = styled.div`
  width: 76px;
  height: 76px;
  cursor: pointer;
  margin: 0 ${({ theme }) => theme.spacing.spacing120}
    ${({ theme }) => theme.spacing.spacing120} 0;
  display: flex;
  border-radius: ${({ theme }) => theme.borders.radius30};
  justify-content: center;
  align-items: center;

  .active {
    border: ${({ theme }) => theme.borders.radius10} solid
      ${({ theme }) => hexToRgb(theme.colors.textPrimary, 0.92)};
    width: 76px;
    height: 76px;
    display: flex;
    justify-content: center;
    align-items: center;
    img {
      width: 62px;
      height: 62px;
      border-radius: ${({ theme }) => theme.borders.radius20};
    }
  }
  &:hover {
    border: ${({ theme }) => theme.borders.radius10} solid
      ${({ theme }) => hexToRgb(theme.colors.textPrimary, 0.64)};
    img {
      width: 62px;
      height: 62px;
      border-radius: ${({ theme }) => theme.borders.radius20};
    }
    div:first-of-type {
      border: none;
    }
  }
`;

const ImageItem = styled.div`
  border-radius: ${({ theme }) => theme.borders.radius30};
  border: ${({ theme }) => theme.borders.radius10} solid
    ${({ theme }) => hexToRgb(theme.colors.textPrimary, 0.16)};
  img {
    width: 76px;
    height: 76px;
    border-radius: 6px;
  }
`;

const VideoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 15px;
  cursor: pointer;
  img {
    border-radius: ${({ theme }) => theme.borders.radius30};
    width: 282px;
    height: 158px;
    object-fit: cover;
  }
  .active {
    border: ${({ theme }) => theme.borders.radius10} solid
      ${({ theme }) => theme.colors.primary20};
    border-radius: ${({ theme }) => theme.borders.radius30};
    img {
      width: 265px;
      height: 141px;
      margin: 7px 0;
      border-radius: ${({ theme }) => theme.borders.radius20};
    }
  }
`;

const VideoTitle = styled.div`
  display: flex;
  color: ${({ theme }) => theme.colors.textPrimary};
  padding-top: ${({ theme }) => theme.spacing.spacing20};
  display: flex;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  width: 285px;
  ${({ theme }) => theme.typography.subTitleMedium};
`;

const VideoDuration = styled.span`
  color: ${({ theme }) => hexToRgb(theme.colors.textPrimary, 0.72)};
  display: flex;
  justify-content: flex-start;
  ${({ theme }) => theme.typography.subTitleSmall};
`;

const RelativeVideoItem = styled.div`
  position: relative;
  justify-content: center;
  align-items: center;
  display: flex;
  svg {
    position: absolute;
    bottom: 12px;
    right: 12px;
    .active {
      color: ${({ theme }) => theme.colors.primary20};
      border-bottom: ${({ theme }) => theme.borders.radius10} solid
        ${({ theme }) => theme.colors.primary20};
    }
  }
  &:hover {
    border: ${({ theme }) => theme.borders.radius10} solid
      ${({ theme }) => hexToRgb(theme.colors.surfaceInverse, 0.72)};
    border-radius: ${({ theme }) => theme.borders.radius30};
    img {
      width: 265px;
      height: 141px;
      margin: 7px 0;
      border-radius: ${({ theme }) => theme.borders.radius20};
    }
    svg {
      display: none;
    }
  }
`;

const NowPlaying = styled.div`
  position: absolute;
  bottom: 7px;
  right: 7px;
  display: flex;
  background-color: ${({ theme }) => theme.colors.surfaceInverse};
  color: ${({ theme }) => theme.colors.primary20};
  align-items: center;
  text-transform: uppercase;
  border: 1px solid ${({ theme }) => hexToRgb(theme.colors.primary20, 0.16)};
  border-top-left-radius: ${({ theme }) => theme.borders.radius20};
  border-bottom-right-radius: ${({ theme }) => theme.borders.radius20};
  padding: ${({ theme }) =>
    `${theme.spacing.spacing20} ${theme.spacing.spacing40}`};
  ${({ theme }) => theme.typography.labelMedium};
`;

const MediaSection = ({
  setSelectedImage,
  setSelectedVideo,
  selectedImage,
  selectedVideo,
  isVideoTab,
}: MediaSectionProps) => {
  const newMedia =
    useSelector((state: State) => selectProductMedia(state)) || [];
  const videos = newMedia.filter((item) => item.mediaType === 'video');
  const relatedVideos =
    useSelector((state: State) => selectProductRelatedVideo(state)) || [];
  const uniqueRelatedVideos = relatedVideos?.filter(
    (relatedVideo) => !videos?.some((video) => video.id === relatedVideo.id)
  );
  const Videos = videos?.concat(uniqueRelatedVideos || []);
  const Images = newMedia.filter((item) => item.mediaType === 'image');

  return (
    <ItemsWrapper isVideo={isVideoTab}>
      {!isVideoTab &&
        Images.map((image) => (
          <ImageWrapper
            key={image.url}
            onClick={() => {
              setSelectedImage(image);
            }}
          >
            <ImageItem
              className={selectedImage.url === image.url ? 'active' : ''}
            >
              <img
                src={image.url}
                alt={image.type}
              />
            </ImageItem>
          </ImageWrapper>
        ))}
      {isVideoTab &&
        Videos.map((video) => (
          <VideoWrapper
            key={video?.videoId}
            onClick={() => {
              setSelectedVideo(video);
            }}
          >
            <RelativeVideoItem
              className={video?.videoId === selectedVideo?.id ? 'active' : ''}
            >
              <img
                src={video.thumbnailUrl}
                alt={video?.videoId}
              />
              {video?.videoId === selectedVideo?.id ? (
                <NowPlaying>Now Playing</NowPlaying>
              ) : (
                <PlayButtonFill />
              )}
            </RelativeVideoItem>
            <VideoTitle>{video?.title}</VideoTitle>
            <VideoDuration>
              {convertSecondsToDisplayTimer(
                video?.duration,
                DisplayUpTo.MINUTES
              )}
            </VideoDuration>
          </VideoWrapper>
        ))}
    </ItemsWrapper>
  );
};
export default MediaSection;
